import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Video, Calendar, DollarSign, Radio, Users, Settings } from 'lucide-react';
import { schedulesAPI, crmAPI, subscriptionAPI, bookingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useThumbnails } from '../hooks/useThumbnails';
import ShareScheduleCard from '../components/ShareScheduleCard';
// Chart imports - uncomment when Stripe integration is complete
// import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { format, parseISO, addDays, startOfDay, startOfWeek, endOfWeek, isToday, isTomorrow } from 'date-fns';

export default function DashboardPage() {
  const { studio } = useAuth();
  const navigate = useNavigate();

  const studioId = studio?.id;

  // Show schedules for the current calendar week (Sunday to Saturday)
  const today = new Date();
  const weekStart = startOfWeek(today, { weekStartsOn: 0 }); // Sunday
  const weekEnd = endOfWeek(today, { weekStartsOn: 0 }); // Saturday

  const { data: schedules, isLoading: schedulesLoading } = useQuery({
    queryKey: ['dashboard-schedules', studioId],
    queryFn: () => schedulesAPI.getSchedules(studioId, {
      startDate: format(weekStart, 'yyyy-MM-dd'),
      endDate: format(weekEnd, 'yyyy-MM-dd')
    }),
    enabled: !!studioId,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    staleTime: 60000, // Cache for 1 minute
    refetchInterval: false
  });

  // Get thumbnails for schedules (cached globally)
  const scheduleList = schedules?.data?.schedules || [];
  const videoIds = useMemo(() =>
    [...new Set(scheduleList.map(s => s.videoId).filter(Boolean))],
    [scheduleList]
  );
  const thumbnailsMap = useThumbnails(videoIds);

  const { data: customersData } = useQuery({
    queryKey: ['customers', studioId],
    queryFn: () => crmAPI.getCustomers(studioId, {}),
    enabled: !!studioId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    staleTime: 30000 // 30 seconds - socket events will invalidate for real-time updates
  });

  const { data: subscription } = useQuery({
    queryKey: ['subscription', studioId],
    queryFn: () => subscriptionAPI.getSubscription(studioId),
    enabled: !!studioId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    staleTime: 60000 // 1 minute
  });

  // Fetch monthly revenue
  const { data: monthlyRevenueData } = useQuery({
    queryKey: ['monthly-revenue', studioId],
    queryFn: () => crmAPI.getMonthlyRevenue(studioId, { months: 12 }),
    enabled: !!studioId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    staleTime: 30000 // 30 seconds - socket events will invalidate for real-time updates
  });

  // Fetch bookings for this week
  const { data: bookingsData } = useQuery({
    queryKey: ['dashboard-bookings', studioId],
    queryFn: () => bookingsAPI.getStudioBookings(studioId, {
      startDate: format(weekStart, 'yyyy-MM-dd'),
      endDate: format(weekEnd, 'yyyy-MM-dd')
    }),
    enabled: !!studioId,
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    staleTime: 60000
  });

  // Calculate real stats from data
  const customers = customersData?.data?.customers || [];
  const payingCustomers = customers.filter(c => c.hasPaidSubscription);
  const monthlyRevenue = payingCustomers.reduce((sum, c) => sum + parseFloat(c.monthlyAmount || 0), 0);
  const scheduledClasses = schedules?.data?.schedules?.filter(s => s.isActive !== false).length || 0;
  const totalBookings = bookingsData?.data?.count || 0;

  // Revenue estimate calculations
  const revenueChartData = monthlyRevenueData?.data?.revenueData || [];
  const totalMonthlyRevenue = customers
    .filter(c => c.hasPaidSubscription)
    .reduce((sum, c) => sum + parseFloat(c.monthlyAmount || 0), 0);
  const yearlyRevenue = monthlyRevenueData?.data?.yearlyRevenue || (totalMonthlyRevenue * 12);
  
  // Get current month/year for display
  const currentDate = new Date();
  const currentMonthYear = currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

  // Check if Stripe is connected (placeholder - would come from studio settings)
  const isStripeConnected = false;

  const stats = [
    {
      label: 'Monthly Revenue',
      value: `$${monthlyRevenue.toFixed(2)}`,
      icon: DollarSign,
      change: '',
      changeType: 'positive'
    },
    {
      label: 'Members',
      value: customers.length,
      icon: Users,
      change: '',
      changeType: 'positive'
    },
    {
      label: 'Classes This Week',
      value: scheduledClasses,
      icon: Calendar,
      change: '',
      changeType: 'positive'
    },
    {
      label: 'Bookings This Week',
      value: totalBookings,
      icon: Users,
      change: '',
      changeType: 'positive'
    }
  ];

  // Calculate next 3 upcoming classes based on date/time
  const upcomingClasses = (() => {
    const allSchedules = schedules?.data?.schedules?.filter(s => s.isActive !== false) || [];
    
    const now = new Date();
    const classesWithNextTime = [];

    allSchedules.forEach(schedule => {
      let scheduleDateTime = null;

      // For one-time schedules, use specificDate
      if (schedule.specificDate) {
        // One-time schedule (backend format)
        const date = parseISO(schedule.specificDate);
        const [hours, minutes] = schedule.startTime.split(':');
        scheduleDateTime = new Date(date);
        scheduleDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      } else if (schedule.dayOfWeek !== null && schedule.dayOfWeek !== undefined) {
        // Recurring weekly schedule
        const today = new Date(now);
        const currentDayOfWeek = today.getDay();
        let daysUntilNext = schedule.dayOfWeek - currentDayOfWeek;
        
        // If the day has passed this week, get next week's occurrence
        const [hours, minutes] = schedule.startTime.split(':');
        const todayAtScheduleTime = new Date(today);
        todayAtScheduleTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        
        if (daysUntilNext < 0 || (daysUntilNext === 0 && todayAtScheduleTime <= now)) {
          daysUntilNext += 7; // Next week
        }
        
        scheduleDateTime = addDays(startOfDay(now), daysUntilNext);
        scheduleDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      }

      if (scheduleDateTime && scheduleDateTime > now) {
        classesWithNextTime.push({
          ...schedule,
          nextOccurrence: scheduleDateTime
        });
      }
    });
    
    // Sort by next occurrence time and take top 3
    const result = classesWithNextTime
      .sort((a, b) => a.nextOccurrence.getTime() - b.nextOccurrence.getTime())
      .slice(0, 3);
    
    return result;
  })();

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Dashboard</h1>
        <p className="text-sm md:text-base mt-1 md:mt-2" style={{ color: 'var(--text-secondary)' }}>Overview of your studio performance</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          // Alternate accent colors for visual interest
          const isPrimary = index === 0;
          const isSecondary = index === 2;

          return (
            <div
              key={index}
              className="group p-5 rounded-2xl transition-all duration-300 transform hover:-translate-y-2"
              style={{
                background: isPrimary
                  ? 'linear-gradient(145deg, var(--primary-12) 0%, var(--primary-5) 100%)'
                  : isSecondary
                  ? 'linear-gradient(145deg, var(--secondary-12) 0%, var(--secondary-5) 100%)'
                  : 'linear-gradient(145deg, var(--highlight-12) 0%, var(--highlight-5) 100%)',
                border: isPrimary
                  ? '1px solid var(--primary-30)'
                  : isSecondary
                  ? '1px solid var(--secondary-30)'
                  : '1px solid var(--border-muted)',
                boxShadow: isPrimary
                  ? '0 0 30px var(--primary-10), 0 4px 20px rgba(0,0,0,0.15)'
                  : isSecondary
                  ? '0 0 30px var(--secondary-10), 0 4px 20px rgba(0,0,0,0.15)'
                  : '0 4px 20px rgba(0,0,0,0.1)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Top shine effect */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 1,
                background: isPrimary
                  ? 'linear-gradient(90deg, transparent, var(--primary-30), transparent)'
                  : isSecondary
                  ? 'linear-gradient(90deg, transparent, var(--secondary-30), transparent)'
                  : 'linear-gradient(90deg, transparent, var(--highlight-15), transparent)',
                pointerEvents: 'none',
              }} />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm mb-1" style={{ color: 'var(--text-muted)' }}>{stat.label}</p>
                  <p className="text-2xl md:text-3xl font-bold" style={{
                    color: isPrimary ? 'var(--primary)' : isSecondary ? 'var(--secondary)' : 'var(--text-primary)'
                  }}>{stat.value}</p>
                  {stat.change && (
                    <p className="text-sm mt-2" style={{ color: stat.changeType === 'positive' ? 'var(--secondary)' : '#ef4444' }}>
                      {stat.change} from last month
                    </p>
                  )}
                </div>
                <div
                  className="w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                  style={{
                    background: isPrimary
                      ? 'linear-gradient(135deg, var(--primary-hover), var(--primary))'
                      : isSecondary
                      ? 'linear-gradient(135deg, var(--secondary-hover), var(--secondary))'
                      : 'linear-gradient(135deg, var(--accent-hover), var(--accent))',
                    boxShadow: isPrimary
                      ? '0 4px 12px var(--primary-30), 0 0 20px var(--primary-15)'
                      : isSecondary
                      ? '0 4px 12px var(--secondary-30), 0 0 20px var(--secondary-15)'
                      : '0 4px 12px var(--accent-30)',
                  }}
                >
                  <Icon className="w-5 h-5 md:w-6 md:h-6" style={{ color: 'var(--bg-base)' }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* FEATURE_DISABLED: Trial Banner - re-enable when ready */}
      {/* {studio?.subscriptionStatus === 'trial' && (
        <div
          className="rounded-2xl p-4 md:p-6 transition-all duration-300"
          style={{
            background: 'linear-gradient(145deg, var(--accent-15) 0%, var(--accent-10) 100%)',
            border: '1px solid var(--accent-30)',
            boxShadow: '0 0 40px var(--accent-10), 0 4px 20px rgba(0,0,0,0.1)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background: 'linear-gradient(90deg, transparent, var(--accent-30), var(--highlight-15), var(--accent-30), transparent)',
            pointerEvents: 'none',
          }} />
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <h3 className="text-base md:text-lg font-semibold" style={{ color: 'var(--accent)' }}>Trial Period Active</h3>
              <p className="text-sm md:text-base mt-1" style={{ color: 'var(--text-secondary)' }}>
                Your trial ends on {new Date(studio.trialEndDate).toLocaleDateString()}.
                Upgrade to continue accessing all features.
              </p>
            </div>
            <button
              className="px-4 py-2 rounded-lg font-medium text-sm md:text-base whitespace-nowrap transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, var(--primary-hover), var(--primary))',
                color: 'var(--bg-base)',
                boxShadow: '0 4px 12px var(--primary-30)',
              }}
            >
              Upgrade Now
            </button>
          </div>
        </div>
      )} */}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Upcoming Classes */}
        <div
          className="p-5 rounded-2xl transition-all duration-300"
          style={{
            background: 'linear-gradient(145deg, var(--bg-elevated) 0%, var(--bg-surface) 100%)',
            border: '1px solid var(--border-muted)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12), inset 0 1px 0 var(--highlight-10)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Top shine effect */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background: 'linear-gradient(90deg, transparent, var(--secondary-30), var(--highlight-15), var(--secondary-30), transparent)',
            pointerEvents: 'none',
          }} />
          <h2 className="text-lg md:text-xl font-bold mb-3 md:mb-4" style={{ color: 'var(--text-primary)' }}>Upcoming Classes</h2>
          {schedulesLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 md:gap-4 p-2 md:p-3 rounded-lg animate-pulse" style={{ background: 'var(--highlight-5)' }}>
                  <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg flex-shrink-0" style={{ background: 'var(--highlight-10)' }}></div>
                  <div className="flex-1">
                    <div className="h-4 rounded w-3/4 mb-2" style={{ background: 'var(--highlight-10)' }}></div>
                    <div className="h-3 rounded w-1/2" style={{ background: 'var(--highlight-8)' }}></div>
                  </div>
                </div>
              ))}
            </div>
          ) : upcomingClasses.length > 0 ? (
            <div className="space-y-3">
              {upcomingClasses.map((schedule) => {
                const nextDate = schedule.nextOccurrence;
                let dateText = '';
                if (isToday(nextDate)) {
                  dateText = 'Today';
                } else if (isTomorrow(nextDate)) {
                  dateText = 'Tomorrow';
                } else {
                  dateText = format(nextDate, 'EEEE, MMM d');
                }
                const timeText = format(nextDate, 'h:mm a');

                const isLiveClass = schedule.classType === 'live';
                const hasThumbnail = !isLiveClass && thumbnailsMap[schedule.videoId];
                const classTitle = isLiveClass
                  ? (schedule.liveTitle || 'Live Class')
                  : (schedule.video?.title || schedule.archivedVideoTitle || 'Unknown Video');

                return (
                  <div
                    key={schedule.id}
                    className="flex items-center gap-3 md:gap-4 p-3 rounded-xl group transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
                    style={{
                      background: isLiveClass
                        ? 'linear-gradient(145deg, var(--primary-12) 0%, var(--primary-8) 100%)'
                        : 'linear-gradient(145deg, var(--secondary-12) 0%, var(--secondary-8) 100%)',
                      border: `1px solid ${isLiveClass ? 'var(--primary-30)' : 'var(--secondary-30)'}`,
                      boxShadow: isLiveClass
                        ? '0 0 20px var(--primary-10), 0 2px 8px rgba(0,0,0,0.08)'
                        : '0 0 20px var(--secondary-10), 0 2px 8px rgba(0,0,0,0.08)',
                    }}
                  >
                    <div
                      className="w-12 h-12 md:w-14 md:h-14 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0"
                      style={{
                        background: isLiveClass
                          ? 'linear-gradient(135deg, var(--primary-hover), var(--primary))'
                          : 'linear-gradient(135deg, var(--secondary-hover), var(--secondary))',
                        boxShadow: isLiveClass ? '0 4px 12px var(--primary-20)' : '0 4px 12px var(--secondary-20)',
                      }}
                    >
                      {hasThumbnail ? (
                        <img
                          src={thumbnailsMap[schedule.videoId]}
                          alt={classTitle}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`w-full h-full flex items-center justify-center ${hasThumbnail ? 'hidden' : ''}`}>
                        {isLiveClass ? (
                          <Radio className="w-6 h-6" style={{ color: 'var(--bg-base)' }} />
                        ) : (
                          <Video className="w-6 h-6" style={{ color: 'var(--bg-base)' }} />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-sm md:text-base truncate" style={{ color: 'var(--text-primary)' }}>{classTitle}</h3>
                        {schedule.videoDeleted && (
                          <span
                            className="px-2 py-0.5 text-xs font-semibold rounded flex items-center gap-1"
                            style={{ background: 'var(--highlight-10)', color: 'var(--highlight)', border: '1px solid var(--highlight)' }}
                          >
                            🗑️ Deleted
                          </span>
                        )}
                        {isLiveClass && (
                          <span
                            className="px-2 py-0.5 text-xs font-bold rounded"
                            style={{ background: 'var(--primary)', color: 'var(--bg-base)' }}
                          >
                            LIVE
                          </span>
                        )}
                      </div>
                      <p className="text-xs md:text-sm" style={{ color: 'var(--text-muted)' }}>
                        {dateText} at {timeText}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)' }}>No upcoming classes scheduled</p>
          )}
        </div>

        {/* Quick Actions */}
        <div
          className="p-5 rounded-2xl transition-all duration-300"
          style={{
            background: 'linear-gradient(145deg, var(--bg-elevated) 0%, var(--bg-surface) 100%)',
            border: '1px solid var(--border-muted)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12), inset 0 1px 0 var(--highlight-10)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Top shine effect */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 1,
            background: 'linear-gradient(90deg, transparent, var(--primary-30), var(--highlight-15), var(--primary-30), transparent)',
            pointerEvents: 'none',
          }} />
          <h2 className="text-lg md:text-xl font-bold mb-3 md:mb-4" style={{ color: 'var(--text-primary)' }}>Quick Actions</h2>
          <div className="space-y-2 md:space-y-3">
            <button
              onClick={() => navigate('/schedule')}
              className="w-full text-left flex items-center px-4 py-3.5 rounded-xl font-medium transition-all duration-300 text-sm md:text-base hover:-translate-y-0.5"
              style={{
                background: 'linear-gradient(135deg, var(--primary-hover), var(--primary))',
                color: 'var(--bg-base)',
                boxShadow: '0 4px 12px var(--primary-30), 0 0 20px var(--primary-10)',
              }}
            >
              <Calendar className="w-4 h-4 md:w-5 md:h-5 mr-2 md:mr-3" />
              Schedule a New Class
            </button>
            <button
              onClick={() => navigate('/videos')}
              className="w-full text-left flex items-center px-4 py-3.5 rounded-xl font-medium transition-all duration-300 text-sm md:text-base hover:-translate-y-0.5 group"
              style={{
                background: 'linear-gradient(145deg, var(--highlight-8) 0%, var(--highlight-5) 100%)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-muted)',
                boxShadow: 'inset 0 1px 0 var(--highlight-10)',
              }}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 transition-all duration-300 group-hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, var(--secondary-hover), var(--secondary))',
                  boxShadow: '0 2px 8px var(--secondary-20)',
                }}
              >
                <Video className="w-4 h-4" style={{ color: 'var(--bg-base)' }} />
              </div>
              Browse Video Library
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="w-full text-left flex items-center px-4 py-3.5 rounded-xl font-medium transition-all duration-300 text-sm md:text-base hover:-translate-y-0.5 group"
              style={{
                background: 'linear-gradient(145deg, var(--highlight-8) 0%, var(--highlight-5) 100%)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-muted)',
                boxShadow: 'inset 0 1px 0 var(--highlight-10)',
              }}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 transition-all duration-300 group-hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, var(--accent-hover), var(--accent))',
                  boxShadow: '0 2px 8px var(--accent-20)',
                }}
              >
                <Settings className="w-4 h-4" style={{ color: 'var(--bg-base)' }} />
              </div>
              Studio Settings
            </button>
          </div>
        </div>
      </div>

      {/* Share Schedule QR Code */}
      <ShareScheduleCard studioId={studioId} shortCode={studio?.shortCode} />

      {/* Revenue Estimates */}
      {totalMonthlyRevenue > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Revenue Estimate */}
          <div
            className="group p-5 rounded-2xl transition-all duration-300 transform hover:-translate-y-2"
            style={{
              background: 'linear-gradient(145deg, var(--accent-10) 0%, var(--accent-5) 100%)',
              border: '1px solid var(--accent-30)',
              boxShadow: '0 0 40px var(--accent-10), 0 8px 32px rgba(0,0,0,0.15)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Top shine effect */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 1,
              background: 'linear-gradient(90deg, transparent, var(--accent-30), var(--highlight-15), var(--accent-30), transparent)',
              pointerEvents: 'none',
            }} />
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Monthly Revenue Estimate</h2>
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, var(--accent-hover), var(--accent))',
                  boxShadow: '0 4px 12px var(--accent-20)',
                }}
              >
                <DollarSign className="w-6 h-6" style={{ color: 'var(--bg-base)' }} />
              </div>
            </div>
            <div className="space-y-6">
              {/* Large Revenue Display */}
              <div className="text-center py-8">
                <p className="text-sm mb-2" style={{ color: 'var(--text-muted)' }}>Current Monthly Revenue</p>
                <p className="text-5xl font-bold" style={{ color: 'var(--primary)' }}>
                  ${totalMonthlyRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>
                  {currentMonthYear}
                </p>
              </div>

              {/* Additional Info */}
              <div
                className="rounded-lg p-4 space-y-2"
                style={{ background: 'var(--accent-10)', border: '1px solid var(--accent-20)' }}
              >
                <div className="flex justify-between items-center">
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Paying Customers</span>
                  <span className="text-lg font-semibold" style={{ color: 'var(--accent)' }}>{payingCustomers.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Average per Customer</span>
                  <span className="text-lg font-semibold" style={{ color: 'var(--accent)' }}>
                    ${payingCustomers.length > 0 ? (totalMonthlyRevenue / payingCustomers.length).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                  </span>
                </div>
                <div style={{ borderTop: '1px solid var(--accent-20)', paddingTop: '0.5rem', marginTop: '0.5rem' }}>
                  <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
                    Revenue from active subscriptions
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Yearly Revenue Estimate */}
          <div
            className="group p-5 rounded-2xl transition-all duration-300 transform hover:-translate-y-2"
            style={{
              background: 'linear-gradient(145deg, var(--secondary-12) 0%, var(--secondary-5) 100%)',
              border: '1px solid var(--secondary-30)',
              boxShadow: '0 0 40px var(--secondary-10), 0 8px 32px rgba(0,0,0,0.15)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Top shine effect */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 1,
              background: 'linear-gradient(90deg, transparent, var(--secondary-30), var(--highlight-15), var(--secondary-30), transparent)',
              pointerEvents: 'none',
            }} />
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Yearly Revenue Est.</h2>
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, var(--secondary-hover), var(--secondary))',
                  boxShadow: '0 4px 12px var(--secondary-20)',
                }}
              >
                <DollarSign className="w-6 h-6" style={{ color: 'var(--bg-base)' }} />
              </div>
            </div>
            <div className="space-y-6">
              {/* Large Revenue Display */}
              <div className="text-center py-8">
                <p className="text-sm mb-2" style={{ color: 'var(--text-muted)' }}>Projected Annual Revenue</p>
                <p className="text-5xl font-bold" style={{ color: 'var(--secondary)' }}>
                  ${yearlyRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>
                  Based on current monthly revenue
                </p>
              </div>

              {/* Breakdown */}
              <div
                className="rounded-lg p-4 space-y-3"
                style={{ background: 'var(--secondary-10)', border: '1px solid var(--secondary-20)' }}
              >
                <div className="flex justify-between items-center">
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Quarterly Estimate</span>
                  <span className="text-lg font-semibold" style={{ color: 'var(--secondary)' }}>
                    ${(yearlyRevenue / 4).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Annual Projection</span>
                  <span className="text-lg font-semibold" style={{ color: 'var(--secondary)' }}>
                    ${yearlyRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Revenue Chart - Requires Stripe Connection */}
      <div
        className="p-5 rounded-2xl transition-all duration-300"
        style={{
          background: 'linear-gradient(145deg, var(--bg-elevated) 0%, var(--bg-surface) 100%)',
          border: '1px solid var(--border-muted)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12), inset 0 1px 0 var(--highlight-10)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Top shine effect */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          background: 'linear-gradient(90deg, transparent, var(--accent-30), var(--highlight-15), var(--accent-30), transparent)',
          pointerEvents: 'none',
        }} />
        <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Revenue Overview</h2>
        {isStripeConnected ? (
          <div className="h-80">
            {/* Chart would render here when Stripe is connected */}
            <p style={{ color: 'var(--text-secondary)' }}>Revenue chart coming soon</p>
          </div>
        ) : (
          /* FEATURE_DISABLED: Connect Stripe prompt - re-enable when ready */
          <div className="h-64 flex flex-col items-center justify-center text-center px-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{ background: 'var(--primary-12)' }}
            >
              <DollarSign className="w-8 h-8" style={{ color: 'var(--primary)' }} />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Revenue Analytics Coming Soon</h3>
            <p className="max-w-md mb-4" style={{ color: 'var(--text-secondary)' }}>
              Revenue tracking and detailed analytics will be available in a future update.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
